import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d50r0b2opjgqftil4hog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-amber-700 text-white relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-10 sm:mb-12">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl mb-3 sm:mb-4">
              FORREST<br />JAMISON
            </h3>
            <p className="text-amber-100 text-sm">
              Crafting stories that matter
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold text-base mb-3 sm:mb-4">Navigate</h4>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { label: 'Home', action: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
                { label: 'About', action: () => scrollToSection('about') },
                { label: 'Books', action: () => scrollToSection('books') },
                { label: 'Videos', action: () => scrollToSection('videos') },
                { label: 'Contact', action: () => scrollToSection('contact') },
              ].map(item => (
                <li key={item.label}>
                  <button
                    onClick={item.action}
                    className="text-amber-100 text-sm hover:text-white transition-colors cursor-pointer"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="sm:col-span-2 md:col-span-1">
            <h4 className="font-semibold text-base mb-3 sm:mb-4">
              Stay updated on new releases
            </h4>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form>
              <input type="hidden" name="form-name" value="Newsletter Subscription" />
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  disabled={isSubmitting}
                  className="flex-1 min-w-0 px-4 py-3 bg-white/10 border border-white/20 rounded-l-lg text-white placeholder-white/60 focus:outline-none focus:border-white/40 text-sm disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-12 h-12 bg-amber-900 hover:bg-amber-950 rounded-r-lg flex items-center justify-center transition-colors disabled:opacity-50 cursor-pointer flex-shrink-0"
                  aria-label="Subscribe"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-200 text-xs mt-2">Subscribed successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-200 text-xs mt-2">Error. Please try again.</p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-white/20 pt-6 sm:pt-8">
          <p className="text-amber-100 text-xs sm:text-sm text-center md:text-left">
            © 2025 Forrest Jamison. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
